import type { ChartData, ChartOptions } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
  categoryAxis,
  chartSummary,
  legendOptions,
  tooltipOptions,
  valueAxis,
} from '../charts/chart-options.ts';
import { ChartFrame, hasChartData } from '../charts/chart-frame.tsx';
import { seriesColor, type ChartBaseProps } from '../charts/palette.ts';
import { registerCharts } from '../charts/register.ts';
import { useThemeColors } from '../charts/useThemeColors.ts';

registerCharts();

export interface BarChartProps extends ChartBaseProps {
  /*
   * Bars sit on top of each other and the axis reads the total. Only honest
   * when the series are parts of one whole — two independent measures stacked
   * make up a number nothing measures.
   */
  stacked?: boolean;
}

/**
 * A count per category: one bar per label, one group (or stack) per series.
 *
 * Capped bar thickness rather than bars that grow with the container — six
 * categories in a wide card would otherwise draw six slabs, which reads as a
 * chart of something much bigger than six numbers.
 */
export function BarChart({
  series,
  labels,
  height = 220,
  stacked = false,
  yFormat,
  xFormat,
  showLegend,
  ariaLabel,
  emptyTitle = 'No data yet',
  className,
}: BarChartProps) {
  const theme = useThemeColors();

  const data: ChartData<'bar'> = {
    labels: labels.map((label, index) => (xFormat ? xFormat(label, index) : label)),
    datasets: series.map((one, index) => ({
      label: one.label,
      data: one.data,
      backgroundColor: seriesColor(theme.series, one, index),
      borderRadius: 4,
      maxBarThickness: 28,
    })),
  };

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: legendOptions(theme, showLegend ?? series.length > 1),
      tooltip: tooltipOptions(theme, yFormat),
    },
    scales: {
      x: categoryAxis(theme, stacked),
      y: valueAxis(theme, yFormat, stacked),
    },
  };

  return (
    <ChartFrame
      height={height}
      ariaLabel={ariaLabel}
      hasData={hasChartData(series)}
      emptyTitle={emptyTitle}
      summary={chartSummary(ariaLabel, series, labels, yFormat)}
      className={className}
    >
      <Bar data={data} options={options} aria-hidden='true' />
    </ChartFrame>
  );
}
